import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { ChapterHeading, Reveal } from "./Reveal";
import { API } from "../config/site";

const INTERESTS = ["Live in it", "Second home", "Investment", "Still deciding"];
const TIMELINES = ["0-6 months", "6-12 months", "1-2 years", "Just watching"];
const BUDGETS = ["Up to $1M", "$1M – $1.4M", "$1.4M – $2M", "$2M – $3.5M", "$3.5M+", "Prefer not to say"];

const inputCls =
  "w-full bg-transparent border-b border-ivory/30 focus:border-ivory outline-none py-3 text-ivory placeholder:text-ivory/40 text-base transition-colors duration-300 rounded-none";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-ivory/60">{label}</span>
    {children}
  </label>
);

const PriorityForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", timeline: "", price_range: "", tour: false });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/leads`, { ...form, access_code: localStorage.getItem("gate_code_value") || "" });
      setStatus("success");
    } catch (err) {
      setError(err.response?.data?.detail ? "Please check your details and try again." : "Something went wrong — please try again or text Marlene directly.");
      setStatus("idle");
    }
  };

  return (
    <section id="priority-list" className="py-24 md:py-32 bg-teal text-ivory" data-testid="priority-form-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ChapterHeading number="07" overline="Buyer List" title="Join Marlene's Kali'u buyer list." dark />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-14 md:mt-20">
          <Reveal className="lg:col-span-5">
            <p className="font-serif text-xl md:text-2xl font-light leading-relaxed text-ivory/90">
              Takes thirty seconds. You&rsquo;ll get a personal floor-plan walkthrough, my notes on the project, and updates as the developer releases details — plus a text from me to say hello.
            </p>
            <p className="text-sm text-ivory/60 leading-relaxed mt-6">
              No spam, no obligation. Your information stays between you and Marlene.
            </p>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.15}>
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="border border-ivory/30 p-10 md:p-14 text-center"
                  data-testid="form-success-state"
                >
                  <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-ivory/50 mb-6">
                    <Check size={22} strokeWidth={1.5} />
                  </span>
                  <p className="font-serif text-2xl md:text-3xl font-light">You&rsquo;re on the list.</p>
                  <p className="text-ivory/70 mt-4 text-base">I&rsquo;ll text you a quick hello. &mdash; Marlene</p>
                </motion.div>
              ) : (
                <motion.form key="form" exit={{ opacity: 0, y: -12 }} onSubmit={submit} className="space-y-8" data-testid="priority-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <Field label="Name *" testid="field-name">
                      <input required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} data-testid="form-name-input" />
                    </Field>
                    <Field label="Email *" testid="field-email">
                      <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="form-email-input" />
                    </Field>
                    <Field label="Phone *" testid="field-phone">
                      <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="Best number for a text" className={inputCls} data-testid="form-phone-input" />
                    </Field>
                    <Field label="I'm interested in" testid="field-interest">
                      <select required value={form.interest} onChange={set("interest")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="form-interest-select">
                        <option value="" disabled className="text-charcoal">Select one</option>
                        {INTERESTS.map((o) => <option key={o} value={o} className="text-charcoal">{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Timeline" testid="field-timeline">
                      <select required value={form.timeline} onChange={set("timeline")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="form-timeline-select">
                        <option value="" disabled className="text-charcoal">Select one</option>
                        {TIMELINES.map((o) => <option key={o} value={o} className="text-charcoal">{o}</option>)}
                      </select>
                    </Field>
                    <Field label="My budget (optional)" testid="field-budget">
                      <select value={form.price_range} onChange={set("price_range")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="form-budget-select">
                        <option value="" className="text-charcoal">Prefer to discuss later</option>
                        {BUDGETS.map((o) => <option key={o} value={o} className="text-charcoal">{o}</option>)}
                      </select>
                    </Field>
                  </div>

                  <label className="flex items-start sm:items-center gap-3 cursor-pointer group" data-testid="field-tour">
                    <input
                      type="checkbox"
                      checked={form.tour}
                      onChange={(e) => setForm({ ...form, tour: e.target.checked })}
                      className="mt-0.5 sm:mt-0 w-4 h-4 accent-[#FAF9F6] cursor-pointer"
                      data-testid="form-tour-checkbox"
                    />
                    <span className="text-sm text-ivory/85 leading-snug">I&rsquo;d like to tour &mdash; please contact me about scheduling</span>
                  </label>

                  {error && <p className="text-sm text-sand" data-testid="form-error-message">{error}</p>}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="group relative overflow-hidden w-full sm:w-auto bg-ivory text-teal px-10 py-4 text-xs tracking-[0.2em] uppercase font-semibold disabled:opacity-70"
                    data-testid="buyer-list-submit-button"
                  >
                    <span className="absolute inset-0 bg-sand translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                    <span className="relative flex items-center justify-center gap-2">
                      {status === "loading" ? <Loader2 size={15} className="animate-spin" /> : null}
                      {status === "loading" ? "Joining..." : "Join the Buyer List"}
                    </span>
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default PriorityForm;
